import type { PassoProduto } from '@/api';
import { Alert, AlertTitle, Button, Typography } from '@mui/material';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import { montarLinkWhatsappComTexto } from '@/lib/contato';
import { nomeDoPasso } from '@/components/catalogo-kiosk/passos';

interface AvisoPassoIndisponivelProps {
  /** Nome do produto: entra na mensagem do WhatsApp, para a loja saber do que se trata. */
  nomeProduto: string;
  /** O passo obrigatório que ficou sem nenhuma opção. */
  passo: PassoProduto;
  /**
   * WhatsApp da loja. Sem número — ou em dispositivo fixo da loja (totem e tablet de mesa, onde
   * o cliente não tem o celular na mão e o atendente está a dois passos) — o aviso manda chamar
   * o atendente em vez de oferecer um link que não ajuda.
   */
  whatsapp?: string | null;
}

/**
 * O que o cliente vê quando a loja pausou TODAS as opções de um passo obrigatório: o item não
 * pode ser pedido, e a tela diz por que e o que fazer. Antes o passo simplesmente não aparecia,
 * e o produto ficava sem o botão de adicionar sem nenhuma explicação.
 */
export function AvisoPassoIndisponivel({
  nomeProduto,
  passo,
  whatsapp,
}: AvisoPassoIndisponivelProps): JSX.Element {
  const numero = whatsapp?.trim();
  const nome = nomeDoPasso(passo);
  const texto = `Olá! Queria pedir ${nomeProduto}, mas o cardápio diz que não está disponível. Consegue me ajudar?`;

  return (
    <Alert severity="warning" variant="outlined" sx={{ alignItems: 'flex-start' }}>
      <AlertTitle sx={{ fontWeight: 700 }}>Este item está indisponível agora</AlertTitle>

      <Typography variant="body2">
        Nenhuma opção de <strong>{nome}</strong> está disponível, e a escolha é obrigatória neste
        item.
      </Typography>

      {numero ? (
        <Button
          variant="contained"
          color="success"
          size="small"
          startIcon={<WhatsAppIcon />}
          href={montarLinkWhatsappComTexto(numero, texto)}
          target="_blank"
          rel="noopener"
          sx={{ mt: 1.5 }}
        >
          Falar no WhatsApp
        </Button>
      ) : (
        <Typography variant="body2" sx={{ mt: 1 }}>
          Chame um atendente para saber quando volta.
        </Typography>
      )}
    </Alert>
  );
}

export default AvisoPassoIndisponivel;
